<template>
  <div class="product__accordion">
    <div
      v-for="(item, $index) in accordionData"
      :key="item.title"
      class="product__accordion-item"
    >
      <h2
        class="product__accordion-item-title"
        @click="toggle($index)"
      >
        {{ item.title }}
        <MinusCircle v-if="item.show" />
        <PlusCircle v-else />
      </h2>
      <Vue3SlideUpDown
        :key="`${product.handle}-${item.title}`"
        v-model="item.show"
        :duration="accordionDuration"
        class="product__accordion-item-text rte"
        v-html="item.content"
      />
    </div>

    <template v-if="!quickView && !hideReviews">
      <div class="product__accordion-item">
        <h2
          v-scroll-to="{
            el: '#judgeme_product_reviews, .jdgm-rev-widg',
            offset: -150
          }"
          class="product__accordion-item-title"
        >
          {{ titles.reviews }}
          <JudgeMeStars :product="product" />
          <span class="ml-auto product__accordion-down-icon">
            <ArrowLeft />
          </span>
        </h2>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { useProductStore } from '../../pinia/ProductStore'

import { Vue3SlideUpDown } from "vue3-slide-up-down"

import PlusCircle from '../../icons/plus-circle'
import MinusCircle from '../../icons/minus-circle'
import ArrowLeft from '../../icons/arrow-left'

import JudgeMeStars from '../../components/product-judgeme-stars'

export default {
  components: {
    PlusCircle,
    MinusCircle,
    ArrowLeft,
    Vue3SlideUpDown,
    JudgeMeStars
  },
  props: {
    titles: {
      type: Object,
      default: null
    },
    quickView: {
      type: Boolean,
      default: false
    },
    quickViewProduct: {
      type: Object,
      default: null
    },
    quickViewSelectedVariant: {
      type: Object,
      default: null
    },
    hideDescription: {
      type: Boolean,
      default: false
    },
    hideNutrition: {
      type: Boolean,
      default: false
    },
    hideReviews: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      accordionDuration: 300,
      accordionData: []
    }
  },
  computed: {
    ...mapState (useProductStore, [
      'product',
      'combinedListingsProduct'
    ]),

    description () {
      if (this.combinedListingsProduct) {
        return this.combinedListingsProduct.description?.split('[nutrition]')[0].split('[size-guide]')[0]
      } else if (this.quickView) {
        return this.quickViewProduct.isCombinedListingsProduct ? this.quickViewSelectedVariant.description?.split('[nutrition]')[0].split('[size-guide]')[0] : this.quickViewProduct.description?.split('[nutrition]')[0].split('[size-guide]')[0]
      } else {
        return this.product.description?.split('[nutrition]')[0].split('[size-guide]')[0]
      }
    },

    ingredients () {
      if (this.combinedListingsProduct) {
        return this.combinedListingsProduct.metafields.ingredients
      } else if (this.quickView) {
        return this.quickViewProduct.isCombinedListingsProduct ? this.quickViewSelectedVariant.metafields.ingredients : this.quickViewProduct.metafields.ingredients
      } else {
        return this.product.metafields.ingredients
      }
    },
    sizeGuide () {
      if (this.combinedListingsProduct && this.combinedListingsProduct.description?.includes('[size-guide]')) {
        return this.combinedListingsProduct.description.split('[size-guide]')[1]
      } else if (this.quickView) {
        if (this.quickViewProduct.isCombinedListingsProduct) {
          return this.quickViewSelectedVariant.description.split('[size-guide]')[1]
        } else {
          return this.quickViewProduct.description.split('[size-guide]')[1]
        }
      } else if (this.product.description?.includes('[size-guide]')) {
        return this.product.description.split('[size-guide]')[1]
      }
      return null
    },
    nutrition () {
      if (this.combinedListingsProduct) {
        if (this.combinedListingsProduct.description?.includes('<p>[nutrition]</p>')) {
          return this.combinedListingsProduct.description.split('<p>[nutrition]</p>')[1]
        } else if (this.combinedListingsProduct.description?.includes('[nutrition]')) {
          return this.combinedListingsProduct.description.split('[nutrition]')[1]
        }
      } else if (this.quickView) {
        if (this.quickViewProduct.isCombinedListingsProduct) {
          if (this.quickViewSelectedVariant.description?.includes('<p>[nutrition]</p>')) {
            return this.quickViewSelectedVariant.description.split('<p>[nutrition]</p>')[1]
          } else if (this.quickViewSelectedVariant.description?.includes('[nutrition]')) {
            return this.quickViewSelectedVariant.description.split('[nutrition]')[1]
          }
        } else {
          if (this.quickViewProduct.description?.includes('<p>[nutrition]</p>')) {
            return this.quickViewProduct.description.split('<p>[nutrition]</p>')[1]
          } else if (this.quickViewProduct.description?.includes('[nutrition]')) {
            return this.quickViewProduct.description.split('[nutrition]')[1]
          }
        }
      } else {
        if (this.product.description?.includes('<p>[nutrition]</p>')) {
          return this.product.description.split('<p>[nutrition]</p>')[1]
        } else if (this.product.description?.includes('[nutrition]')) {
          return this.product.description.split('[nutrition]')[1]
        }
      }
      return null
    },
    customAccordions () {
      if (this.combinedListingsProduct) {
        return this.combinedListingsProduct.metafields.accordions
      } else if (this.quickView) {
        return this.quickViewProduct.isCombinedListingsProduct ? this.quickViewSelectedVariant.metafields.accordions : this.quickViewProduct.metafields.accordions
      } else {
        return this.product.metafields.accordions
      }
    },
  },
  watch: {
    product() {
      this.getAccordionData()
    },
    quickViewProduct() {
      this.getAccordionData()
    },
    combinedListingsProduct () {
      this.getAccordionData()
    },
    quickViewSelectedVariant () {
      this.getAccordionData()
    }
  },
  mounted () {
    this.getAccordionData()
  },
  methods: {
    getAccordionData () {
      const accordionItems = []

      if (!this.hideDescription) accordionItems.push('description')
      if (this.ingredients) accordionItems.push('ingredients')
      if (this.nutrition && !this.hideNutrition) accordionItems.push('nutrition')
      if (this.sizeGuide) accordionItems.push('sizeGuide')

      this.accordionData = [
        ...accordionItems.map((item, index) => ({
          show: index === 0,
          title: this.titles[item],
          content: this[item]
        })),
      ]

      if (this.customAccordions) {
        this.accordionData.push(...this.customAccordions.map((item) => ({
          show: false,
          title: item.title,
          content: item.content
        })))
      }
    },
    toggle (index) {
      this.accordionData[index].show = !this.accordionData[index].show
    }
  }
}
</script>
